"use client";

// import atcPromoVideo from '/videos/ATC-Sizzle-Reel-WIDE.mp4';
import { cn } from "@/lib/utils";
import BackgroundVideo from "next-video/background-video";
import React, { SyntheticEvent, useEffect, useRef, useState } from "react";
import atcPromoVideo from "/videos/atc-promo-bg-video.mp4";

import { trackEvent } from "@/lib/analyticsUtil";
import { gsap, ScrollTrigger } from "@/lib/gsap";

interface HeroVideoProps {
  className?: string;
  parentContainerRef?: React.RefObject<HTMLElement>;
  /** MDX or other rich text content rendered in the text column */
  children?: React.ReactNode;
}

// TODO: Customizing Video Player video: https://www.media-chrome.org/docs/en/reference/styling
// https://next-video.dev/docs#default-player

export default function HeroVideo({ className, parentContainerRef, children }: HeroVideoProps) {
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const videoSectionRef = useRef<HTMLDivElement>(null);
  const videoPlayerRef = useRef<HTMLVideoElement | null>(null);

  function videoEndCallback(e: SyntheticEvent<HTMLVideoElement>) {
    console.log("[YK-Video] onEnded", e);
  }

  useEffect(() => {
    if (!isVideoLoaded) {
      console.log("[YK-Video] video is not loaded yet");
      return;
    }

    // Determine which ref to use for ScrollTrigger
    const triggerRef = parentContainerRef?.current ? parentContainerRef : videoSectionRef;
    const triggerElement = triggerRef.current;

    if (!triggerElement || !videoPlayerRef.current) {
      if (!triggerElement) {
        console.log("[YK-Video] triggerRef not found");
      }
      if (!videoPlayerRef.current) {
        console.log("[YK-Video] videoPlayerRef not found");
      }
      return;
    }

    // Pause video initially
    // videoPlayerRef.current.pause();

    // Set up ScrollTrigger to play/pause video based on visibility
    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: triggerElement,
        start: "top 50%", // Start when top of section is 50% down the viewport
        end: "bottom 50%", // End when bottom is 50% up the viewport
        onEnter: () => {
          console.log("[YK-Video] onEnter");
          videoPlayerRef.current?.play().catch((error) => {
            console.error("[YK-Video] Play error:", error);
          });
        },
        onEnterBack: () => {
          console.log("[YK-Video] onEnterBack");
          videoPlayerRef.current?.play().catch((error) => {
            console.error("[YK-Video] Play error:", error);
          });
        },
        onLeave: () => {
          console.log("[YK-Video] onLeave");
          videoPlayerRef.current?.pause();
        },
        onLeaveBack: () => {
          console.log("[YK-Video] onLeaveBack");
          videoPlayerRef.current?.pause();
        },
        markers: false,
      });
    }, triggerRef);

    // Cleanup ScrollTrigger on unmount
    return () => ctx.revert();
  }, [isVideoLoaded, parentContainerRef]);

  return (
    <div ref={videoSectionRef} className={cn("max-w-7xl", "mx-auto", "relative", className)}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center overflow-visible">
        <div className="order-2 md:order-1 mb-8">
          <div className="prose prose-lg max-w-none">
            {children}
          </div>
        </div>

        <div
          className={cn(
            "order-1 md:order-2 relative",
            "p-2", // Add padding for spikes
            "overflow-visible",
          )}
        >
          {/* Video Section */}
          <div
            className={cn(
              "relative",
              "aspect-[16/9]",
              "max-w-full w-full",
              "clip-jagged-container",
              "-m-2", // Negative margin to offset padding
            )}
          >
            <div className={cn("absolute inset-0", "clip-jagged")}>
              {/* Background Video */}
              <BackgroundVideo
                ref={videoPlayerRef}
                src={atcPromoVideo}
                // == Playback Events ==
                onPause={(e) => console.debug("[YK-Video] onPause", e)}
                onPlaying={(e) => console.log("[YK-Video] onPlaying", e)}
                onPlay={(e) => console.log("[YK-Video] onPlay", e)}
                onEnded={videoEndCallback}
                // == Video Player Init Events ==

                onLoadedMetadata={() => {
                  console.log("[YK-Video] onLoadedMetadata | ref:", videoPlayerRef.current);
                  setIsVideoLoaded(true);
                  if (videoPlayerRef.current) {
                    console.log("[YK-Video] Video duration:", videoPlayerRef.current.duration);
                  }
                }}
                onLoadedData={() => {
                  console.log("[YK-Video] onLoadedData | ref:");
                }}
                onCanPlay={() => {
                  console.log("[YK-Video] onCanPlay | ref:");
                }}
                onError={(e) => {
                  console.warn("[YK-Video] onError:", e);
                  // TODO: double check how `video_error` gets saved in GA4
                  trackEvent("video_error", { error: e });
                }}
                poster="/images/logo/atc-video-title-card.webp"
                autoPlay={false}
                muted
                loop={false}
                controls={false}
                className="w-full h-full object-contain z-0"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
